import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { zIndexMap } from '../../../shared/config';
import {
  resolveCurrencyPrefix,
  resolveThousandsSeparator,
} from '../../../shared/model';
import { rubikTypographyStyles } from '../../../shared/ui';

import { MyPageProfileEditField } from './my-page-profile-edit-field';
import { MyPageProfileEditTopRow } from './my-page-profile-edit-top-row';
import { MyPageProfileGoalResetTip } from './my-page-profile-goal-reset-tip';

type MyPageProfileEditSheetProps = {
  avatarUrl?: string | null;
  countryCode: '+54' | '+55';
  goalAmountValue: string;
  goalText: string;
  isRatingVisible: boolean;
  onAvatarEditPress: () => void;
  onGoalAmountSave?: (nextValue: string) => void;
  onGoalTextSave?: (nextValue: string) => void;
  onRatingVisibilityChange: (isVisible: boolean) => void;
  ratingValueLabel: string;
};

const GOAL_TEXT_MAX_LENGTH = 200;

const Sheet = styled.section.attrs({ 'data-ui': 'my-page-profile-edit-sheet' })`
  position: relative;
  border-radius: ${({ theme }) => theme.scale.px(15)}
    ${({ theme }) => theme.scale.px(15)} 0 0;
  background: ${({ theme }) => theme.colors.surface.base};
  box-shadow: 0 -6px 10px rgba(0, 0, 0, 0.06);
  padding: ${({ theme }) => theme.scale.px(16)}
    ${({ theme }) => theme.scale.px(30)} ${({ theme }) => theme.scale.px(24)};
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(15)};

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: ${({ theme }) => `-${theme.scale.px(12)}`};
    left: 0;
    height: ${({ theme }) => theme.scale.px(12)};
    background: ${({ theme }) => theme.colors.surface.base};
    pointer-events: none;
  }
`;

const Title = styled.h2`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const FieldsCluster = styled.div`
  position: relative;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(15)};
`;

const GoalResetTipOverlay = styled(MyPageProfileGoalResetTip)`
  position: absolute;
  top: ${({ theme }) => `-${theme.scale.px(40)}`};
  right: ${({ theme }) => theme.scale.px(5)};
  z-index: ${zIndexMap.myPageProfile.goalResetTipOverlay};
`;

export function MyPageProfileEditSheet({
  avatarUrl = null,
  countryCode,
  goalAmountValue,
  goalText,
  isRatingVisible,
  onAvatarEditPress,
  onGoalAmountSave,
  onGoalTextSave,
  onRatingVisibilityChange,
  ratingValueLabel,
}: MyPageProfileEditSheetProps) {
  const { t } = useTranslation();
  const [isGoalTipVisible, setIsGoalTipVisible] = useState(false);
  const goalAmountCurrencyPrefix = resolveCurrencyPrefix(countryCode);
  const goalAmountThousandsSeparator = resolveThousandsSeparator(countryCode);

  return (
    <Sheet>
      <Title>{t('myPage.editTitle')}</Title>

      <MyPageProfileEditTopRow
        avatarUrl={avatarUrl}
        avatarEditAriaLabel={t('myPage.avatarEditAria')}
        isRatingVisible={isRatingVisible}
        onAvatarEditPress={onAvatarEditPress}
        ratingLabel={t('myPage.ratingLabel')}
        ratingValueLabel={ratingValueLabel}
        onRatingVisibilityChange={onRatingVisibilityChange}
      />

      <FieldsCluster>
        <MyPageProfileEditField
          key={`goal:${goalText}`}
          label={t('myPage.goalLabel')}
          infoAriaLabel={t('myPage.goalInfoAria')}
          maxLength={GOAL_TEXT_MAX_LENGTH}
          onInfoPress={() => {
            setIsGoalTipVisible(currentValue => !currentValue);
          }}
          onValueSave={onGoalTextSave}
          typographyVariant="compact"
          value={goalText}
        />
        {isGoalTipVisible && <GoalResetTipOverlay />}
        <MyPageProfileEditField
          key={`amount:${goalAmountValue}`}
          currencyPrefix={goalAmountCurrencyPrefix}
          label={t('myPage.amountLabel')}
          onValueSave={onGoalAmountSave}
          thousandsSeparator={goalAmountThousandsSeparator}
          value={goalAmountValue}
        />
      </FieldsCluster>
    </Sheet>
  );
}
